import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation, Link } from 'react-router-dom';

import Crud from '../../common/crud';
import Loader from '../../common/loader';
import NotFound from '../../common/not-found';
import Trash from '../../common/trash';

import AuthContext from '../../context';
import { del, get, post, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    tableContainer: {
        width: '100%',
    }
}));

const Roles = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [state, setState] = useState({
        loading: true,
        roles: [],
        role: {
            idrole: 0,
            name: '',
        },
        permissions: {},
    });

    useEffect(() => {
        window.document.title = 'Roles';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions?.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/roles');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, roles: response?.data ?? [] }));
        } else {
            console.log('[Roles] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, role: { ...prev.role, [target.name]: target.value } }));

    const validateForm = (type) => {
        const { idrole, name } = state.role;

        switch (type) {
            case 'update':
                const data = state.roles.filter(value => value.idrole === idrole);

                if (data[0]) {
                    const before = JSON.stringify(data[0]);
                    const after = JSON.stringify(state.role);
                    if (before !== after) {
                        if (name.trim()) {
                            return true;
                        } else {
                            showToast('All fields is required', 'error');
                            return false;
                        }
                    } else {
                        showToast('Update some field', 'error');
                        return false;
                    }
                } else {
                    showToast('Select a data', 'error');
                    return false;
                }

            case 'store':

                if (name.trim()) {
                    return true;
                } else {
                    showToast('All fields is required', 'error');
                    return false;
                }

            case 'delete':
                if (idrole === 0) {
                    showToast('Select a data', 'error');
                    return false;
                }
                const cond = window.confirm('Are you sure you want to delete this role?');

                return Boolean(idrole && cond);
            default:
                break;
        }
    }

    const clear = () => {
        setState(prev => ({ ...prev, role: { ...prev.role, idrole: 0, name: '' } }));
    }

    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/role/store', state.role);
            if (response.status === 200) {
                setState(prev => ({ ...prev, roles: response?.data ?? prev.roles }));
                showToast(response.message);
            } else {
                console.log('[Roles] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false
            }
        }
        showLoader(false);

        return cond;
    }

    const update = async () => {
        const { idrole } = state.role;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/role/${idrole}/update`, state.role);
            if (response.status === 200) {
                setState(prev => ({ ...prev, roles: response?.data ?? prev.roles }));
                showToast(response.message);
            } else {
                console.log('[Roles] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { idrole } = state.role;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/role/${idrole}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, roles: response?.data ?? prev.roles }));
                showToast(response.message);
            } else {
                console.log('[Roles] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const renderCell = ({ row }) => {
        return (
            <Link to={`/history/roles/${row.idrole}`} target='_blank'>
                <Tooltip title='Show history' placement='top'>
                    <IconButton>
                        <Icon>{'history'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'actions', headerName: 'Actions', flex: 0.3, renderCell }
    ];

    if (state.loading) {
        return <Loader />;
    }

    if (!state.permissions.read_attr) {
        return <NotFound />;
    }

    return (
        <div className={classes.root}>
            <h1>{'Roles'}</h1>
            <div>
                <Grid container>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>{'Name'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Name'
                                    name='name'
                                    value={state.role.name}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            {
                state.permissions.delete_attr &&
                <Trash table='roles' id='idrole' onRefresh={makeRequest} />
            }

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.roles}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idrole}
                    onRowClick={({ row }) => setState(prev => ({ ...prev, role: row }))}
                />
            </div>
        </div>
    );
}

export default Roles;